/**
 * Defensive accessor for create-response bodies in offline sync paths.
 *
 * `api.post()` returns `res.data` — the raw JSON body, with no envelope added
 * by the client (see services/api.ts). The BACKEND is inconsistent about
 * whether it envelopes:
 *
 *   bare      incident.controller.ts   res.status(201).json(incident)
 *             infrastructure.controller.ts  res.status(201).json(closure)
 *   enveloped damage-assessment.controller.ts  res.status(201).json({ data: assessment })
 *
 * Offline services that assumed the enveloped shape read `response.data.id` on a
 * bare response, got `undefined`, and threw — AFTER the server had already
 * committed the row. The local record stayed unsynced with its offline id, so
 * every later sync POSTed it again: unbounded duplicates on the EOC map, and for
 * incidents the child FK remap never ran either.
 *
 * Accepting both shapes makes sync robust to that inconsistency instead of
 * silently depending on it.
 */
export function unwrapEntity<T extends Record<string, unknown>>(response: unknown): T | null {
  if (!response || typeof response !== 'object') return null;
  const r = response as Record<string, unknown>;
  // Enveloped: { data: { ... } }. Only unwrap when `data` is itself an object,
  // so a bare entity that happens to carry a `data` field is not mangled.
  if (r.data && typeof r.data === 'object' && !Array.isArray(r.data)) {
    return r.data as T;
  }
  return r as T;
}

/**
 * Server id from a create response, accepting either envelope shape.
 * Returns null when absent so callers can fail loudly rather than writing
 * `undefined` into IndexedDB as an id.
 */
export function unwrapServerId(response: unknown): string | null {
  const entity = unwrapEntity<{ id?: unknown }>(response);
  const id = entity?.id;
  return typeof id === 'string' && id.length > 0 ? id : null;
}
